import { createContext, forwardRef, useContext, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * The UAE Design System 3.0 button (`aegov-btn`): 8px corners (6px small, 4px extra small), a medium-weight label, a solid fill with a
 * faint inset highlight, and the Design System's focus outline. Variants map onto it:
 *   default, gold  btn (primary): AE Gold 600
 *   outline        btn-outline: a 2px AE Gold 600 edge, AE Gold 700 label
 *   secondary      btn-secondary btn-outline: a 2px AE Black 800 edge
 *   ghost          btn-secondary btn-link: no edge, AE Black 800 label, a grey fill on hover
 *   link           a Design System text link
 *   destructive, success  the primary shape in AE Red 700 / AE Green 700
 * Sizes are the Design System's heights: sm 32px (btn-xs), default 40px (btn-sm), lg 48px (btn-base), xl 52px (btn-lg). The one
 * departure: on hover a solid button darkens a step instead of lightening to AE Gold 500, which would leave its white label under
 * 4.5:1.
 */
const solid = "border border-transparent text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.2)] disabled:opacity-30";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium select-none transition-[background-color,border-color,color,box-shadow,text-decoration-color] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: `${solid} bg-aegold-600 hover:bg-aegold-700`,
        gold: `${solid} bg-aegold-600 hover:bg-aegold-700`,
        secondary: "border-2 border-aeblack-800 bg-transparent text-aeblack-800 hover:bg-aeblack-100",
        outline: "border-2 border-aegold-600 bg-transparent text-aegold-700 hover:bg-aegold-50",
        ghost: "text-aeblack-800 hover:bg-aeblack-100/70 hover:text-strong",
        link: "text-aegold-700 underline decoration-1 underline-offset-2 hover:text-aegold-800 hover:decoration-2",
        destructive: `${solid} bg-aered-700 hover:bg-aered-800`,
        success: `${solid} bg-aegreen-700 hover:bg-aegreen-800`,
        glass: "border border-aeblack-100 bg-white/90 text-strong backdrop-blur-sm hover:bg-white",
      },
      size: {
        default: "h-10 rounded-md px-5 text-base [&_svg]:size-5",
        sm: "h-8 rounded-sm px-3.5 text-sm [&_svg]:size-4",
        lg: "h-12 rounded-lg px-6 text-base [&_svg]:size-5",
        xl: "h-13 rounded-lg px-7 text-lg [&_svg]:size-6",
        icon: "size-10 rounded-md [&_svg]:size-5",
        "icon-sm": "size-8 rounded-sm [&_svg]:size-4",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

/**
 * A screen shows one primary action. Inside a `secondary` scope the primary styles (default and gold) render as outline buttons,
 * so only a button marked `primary` stands out; dialogs and side panels open a `primary` scope of their own, where their submit
 * button is the one primary action of that surface.
 */
const Emphasis = createContext<"primary" | "secondary">("primary");

export function ActionScope({ emphasis, children }: { emphasis: "primary" | "secondary"; children: ReactNode }) {
  return <Emphasis.Provider value={emphasis}>{children}</Emphasis.Provider>;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** The screen's one primary action: keeps its primary style inside a secondary scope. */
  primary?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, primary = false, children, disabled, ...props }, ref) => {
    const scope = useContext(Emphasis);
    const strong = variant == null || variant === "default" || variant === "gold";
    const shown = strong && scope === "secondary" && !primary ? "outline" : variant;
    const marker = strong && shown !== "outline" ? { "data-primary-action": "" } : {};
    if (asChild) {
      return (
        <Slot.Root ref={ref} className={cn(buttonVariants({ variant: shown, size }), className)} {...marker} {...props}>
          {children}
        </Slot.Root>
      );
    }
    return (
      <button ref={ref} className={cn(buttonVariants({ variant: shown, size }), className)} disabled={disabled || loading} {...marker} {...props}>
        {loading ? <Loader2 className="animate-spin" /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
